import dayjs from "dayjs";
import { useCallback, useEffect } from "react";
import { useFreelancerContext } from "../contexts/useFreelancerContext";
import { useNavigationInterceptorContext } from "../contexts/useNavigationInterceptorContext";
import { FreelancerOnboardingFormStep } from "../models/enums/FreelancerOnboardingFormStep";

/**
 * Hook that detects if the user has an invalid mission end date
 * and manages the dialog state for prompting correction
 */
export const useInvalidMissionEndDateValidation = () => {
	const {
		currentOnboardingFormStep,
		freelancerProfileQueryResult,
		freelancerOnboardingMyAvailabilityStepViewModel,
		showInvalidMissionEndDateDialog,
		invalidMissionEndDateNextStep,
		setShowInvalidMissionEndDateDialog,
		setInvalidMissionEndDateNextStep
	} = useFreelancerContext();

	const { data: freelancerProfile } = freelancerProfileQueryResult;
	const { blockNavigation } = useNavigationInterceptorContext();

	// Check if initial mission end date is invalid
	const hasInvalidMissionEndDate = useCallback(() => {
		// Only check when on MyAvailability step
		if (currentOnboardingFormStep !== FreelancerOnboardingFormStep.MyAvailability) {
			return false;
		}

		// Don't check if form has been modified
		if (freelancerOnboardingMyAvailabilityStepViewModel?.form?.isDirty) {
			return false;
		}

		if (!freelancerProfile?.availability) return false;

		const { isAvailable, missionEndDate } = freelancerProfile.availability;

		// Only check if user is on mission
		if (isAvailable !== false) return false;

		// Check if date exists and is in the past
		if (missionEndDate) {
			return dayjs(missionEndDate).isBefore(dayjs().subtract(1, "day"));
		}

		return false;
	}, [
		currentOnboardingFormStep,
		freelancerProfile,
		freelancerOnboardingMyAvailabilityStepViewModel
	]);

	useEffect(() => {
		setTimeout(() => {
			blockNavigation(hasInvalidMissionEndDate());
		}, 1000);
	}, [blockNavigation, hasInvalidMissionEndDate]);

	const displayInvalidDateDialog = (
		nextOnboardingFormStep: FreelancerOnboardingFormStep | null
	) => {
		setShowInvalidMissionEndDateDialog(true);
		setInvalidMissionEndDateNextStep(nextOnboardingFormStep);
	};

	const confirmContinueAnyway = () => {
		setShowInvalidMissionEndDateDialog(false);
		blockNavigation(false);
	};

	const cancel = () => {
		setShowInvalidMissionEndDateDialog(false);
	};

	return {
		hasInvalidMissionEndDate: hasInvalidMissionEndDate(),
		showInvalidMissionEndDateDialog,
		nextOnboardingFormStep: invalidMissionEndDateNextStep,
		displayInvalidDateDialog,
		confirmContinueAnyway,
		cancel
	};
};
